/**
 * Extrude kernel — profile regions → prism/tapered-prism meshes.
 *
 * Each end cap can carry a scale factor about the region's area centroid,
 * interpolated linearly along the height: (1, 1) is a straight prism,
 * (1, 0) collapses the top to an apex (circle → cone, rect → pyramid),
 * (1, 0.5) is a frustum, >1 flares. Caps are earcut-triangulated (holes
 * supported, and holes scale about the same centroid so cones stay
 * concentric); walls are flat-shaded quads with geometric normals so
 * tapered facets shade correctly. A scaled-to-zero end skips its cap and
 * its wall quads degrade to apex triangles — the solid stays watertight
 * for the Manifold boolean engine.
 */

import earcut from 'earcut';

import type { MeshData, ProfileRegion } from './types';
import type { Vec2 } from '../timeline/types';

/** Dihedral threshold (degrees) above which an outline corner gets a rail edge. */
const CORNER_EDGE_DEG = 30;
const SCALE_EPS = 1e-6;

interface MeshAccumulator {
  positions: number[];
  normals: number[];
  indices: number[];
  edges: number[];
}

export function extrudeProfiles(
  regions: readonly ProfileRegion[],
  wBottom: number,
  wTop: number,
  scaleBottom = 1,
  scaleTop = 1,
): MeshData {
  if (wTop < wBottom) {
    [wBottom, wTop] = [wTop, wBottom];
    [scaleBottom, scaleTop] = [scaleTop, scaleBottom];
  }
  const acc: MeshAccumulator = { positions: [], normals: [], indices: [], edges: [] };
  for (const region of regions) {
    extrudeRegion(region, wBottom, wTop, scaleBottom, scaleTop, acc);
  }
  return {
    positions: new Float32Array(acc.positions),
    normals: new Float32Array(acc.normals),
    indices: new Uint32Array(acc.indices),
    edgePositions: new Float32Array(acc.edges),
  };
}

/** Area centroid of a polygon loop (shoelace-weighted). */
export function centroidOf(loop: readonly (readonly [number, number])[]): Vec2 {
  let area = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < loop.length; i++) {
    const [x0, y0] = loop[i]!;
    const [x1, y1] = loop[(i + 1) % loop.length]!;
    const w = x0 * y1 - x1 * y0;
    area += w;
    cx += (x0 + x1) * w;
    cy += (y0 + y1) * w;
  }
  if (Math.abs(area) < 1e-12) return loop[0] ?? [0, 0];
  return [cx / (3 * area), cy / (3 * area)];
}

function extrudeRegion(
  region: ProfileRegion,
  wBottom: number,
  wTop: number,
  scaleBottom: number,
  scaleTop: number,
  acc: MeshAccumulator,
): void {
  const m = region.planeToWorld;
  const n: [number, number, number] = [m[8]!, m[9]!, m[10]!];
  const center = centroidOf(region.outer);

  /** Profile point → world, scaled about the centroid, lifted along normal. */
  const place = (p: readonly [number, number], w: number, s: number): [number, number, number] => {
    const u = center[0] + (p[0] - center[0]) * s;
    const v = center[1] + (p[1] - center[1]) * s;
    return [
      m[0]! * u + m[4]! * v + m[12]! + n[0] * w,
      m[1]! * u + m[5]! * v + m[13]! + n[1] * w,
      m[2]! * u + m[6]! * v + m[14]! + n[2] * w,
    ];
  };

  // --- caps ---
  const flat: number[] = [];
  const holeStarts: number[] = [];
  for (const p of region.outer) flat.push(p[0], p[1]);
  for (const hole of region.holes) {
    holeStarts.push(flat.length / 2);
    for (const p of hole) flat.push(p[0], p[1]);
  }
  const tris = earcut(flat, holeStarts.length ? holeStarts : undefined);
  const capVerts: (readonly [number, number])[] = [];
  for (let i = 0; i < flat.length; i += 2) capVerts.push([flat[i]!, flat[i + 1]!]);

  for (const [w, s, sign] of [
    [wTop, scaleTop, 1],
    [wBottom, scaleBottom, -1],
  ] as const) {
    if (s < SCALE_EPS) continue; // collapsed to an apex — no cap
    const base = acc.positions.length / 3;
    for (const p of capVerts) {
      acc.positions.push(...place(p, w, s));
      acc.normals.push(n[0] * sign, n[1] * sign, n[2] * sign);
    }
    for (let i = 0; i < tris.length; i += 3) {
      if (sign > 0) acc.indices.push(base + tris[i]!, base + tris[i + 1]!, base + tris[i + 2]!);
      else acc.indices.push(base + tris[i]!, base + tris[i + 2]!, base + tris[i + 1]!);
    }
  }

  // --- walls + edges ---
  const hasHeight = wTop - wBottom > 1e-9;
  const cosThreshold = Math.cos((CORNER_EDGE_DEG * Math.PI) / 180);

  for (const loop of [region.outer, ...region.holes]) {
    const count = loop.length;
    for (let i = 0; i < count; i++) {
      const p0 = loop[i]!;
      const p1 = loop[(i + 1) % count]!;
      const b0 = place(p0, wBottom, scaleBottom);
      const b1 = place(p1, wBottom, scaleBottom);
      const t0 = place(p0, wTop, scaleTop);
      const t1 = place(p1, wTop, scaleTop);

      // Rim edges only where a cap actually exists.
      if (scaleBottom >= SCALE_EPS) acc.edges.push(...b0, ...b1);
      if (scaleTop >= SCALE_EPS) acc.edges.push(...t0, ...t1);

      if (hasHeight) {
        pushQuad(b0, b1, t1, t0, acc);
        // Corner rail at p1 if the outline turns sharply there.
        const p2 = loop[(i + 2) % count]!;
        const d0x = p1[0] - p0[0], d0y = p1[1] - p0[1];
        const d1x = p2[0] - p1[0], d1y = p2[1] - p1[1];
        const l0 = Math.hypot(d0x, d0y) || 1;
        const l1 = Math.hypot(d1x, d1y) || 1;
        if ((d0x / l0) * (d1x / l1) + (d0y / l0) * (d1y / l1) < cosThreshold) {
          acc.edges.push(...b1, ...t1);
        }
      }
    }
  }
}

/** Quad with geometric flat normal; degenerate triangles skipped (apex). */
function pushQuad(
  a: readonly number[],
  b: readonly number[],
  c: readonly number[],
  d: readonly number[],
  acc: MeshAccumulator,
): void {
  const n = triNormal(a, b, c) ?? triNormal(a, c, d);
  if (!n) return;
  const base = acc.positions.length / 3;
  for (const v of [a, b, c, d]) {
    acc.positions.push(v[0]!, v[1]!, v[2]!);
    acc.normals.push(...n);
  }
  if (triNormal(a, b, c)) acc.indices.push(base, base + 1, base + 2);
  if (triNormal(a, c, d)) acc.indices.push(base, base + 2, base + 3);
}

function triNormal(
  a: readonly number[],
  b: readonly number[],
  c: readonly number[],
): [number, number, number] | null {
  const ux = b[0]! - a[0]!, uy = b[1]! - a[1]!, uz = b[2]! - a[2]!;
  const vx = c[0]! - a[0]!, vy = c[1]! - a[1]!, vz = c[2]! - a[2]!;
  const nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
  const len = Math.hypot(nx, ny, nz);
  if (len < 1e-10) return null;
  return [nx / len, ny / len, nz / len];
}
